import { getConfig } from '@/config';
import i18n from '@/i18n';
import { fortnoxAuthService } from '@/services/auth/fortnox-auth-service';
import { notificationService } from '@/services/notification/notification-service';
import { handleHttpErrors } from '@/utils/errors/converters/system-converter-errors';
import { ErrorSource, HttpService, HttpStatus } from 'cesg-shared';
import type { FetchLike, WretchOptions } from 'wretch';
import type { WretchError } from 'wretch/resolver';
import { apiRequest, entraAuthService } from '../auth/entra-auth-service';

function authorizationEntraIDHeader(next: FetchLike) {
  return async (url: string, options: WretchOptions) => {
    if (entraAuthService.isLoggedIn.value) {
      const token = await entraAuthService.acquireToken(apiRequest.scopes);
      options.headers.authorization = `Bearer ${token}`;
    } else {
      delete options.headers.entraauthorization;
    }

    return next(url, options);
  };
}

const authorizationFortnoxHeader =
  (next: FetchLike) => async (url: string, options: WretchOptions) => {
    try {
      const token = await fortnoxAuthService.getAccessToken();
      options.headers.fortnoxauthorization = `Bearer ${token}`;
      return next(url, options);
    } catch {
      fortnoxAuthService.logout();
      fortnoxAuthService.login();
      return new Response('Återställer koppling med fortnox', {
        status: HttpStatus.Unauthorized,
      });
    }
  };

async function forbiddenCallback(error: WretchError) {
  const { message } = error.json ?? {};
  notificationService.open({
    text: message ?? 'Unknown error',
    type: 'danger',
  });
  throw error;
}

const { t } = i18n.global;
function badRequestCallback(error: WretchError) {
  const { source, message, slug } = error.json ?? {};
  const sources = [ErrorSource.Fortnox, ErrorSource.Server];

  if (source && sources.includes(source) && message) {
    const actualMessage = slug
      ? t(`notifications.cesgErrorSlug.${slug}`)
      : message;

    notificationService.open({
      text: `${source}: ${actualMessage}`,
      type: 'warning',
      timeout: 10000,
    });
  }

  throw error;
}

class AppHttpService {
  http: HttpService;

  constructor() {
    this.http = new HttpService(`${getConfig().BACKEND_URL}/api`)
      .middlewares(authorizationEntraIDHeader)
      .catcher(HttpStatus.Forbidden, forbiddenCallback)
      .catcher(HttpStatus.BadRequest, badRequestCallback)
      .catcherFallback(handleHttpErrors);
  }

  get request(): typeof this.http.request {
    return this.http.request;
  }
}

class FortnoxHttpService extends AppHttpService {
  constructor() {
    super();
    this.http = this.http.middlewares(authorizationFortnoxHeader);
  }
}

class AuthHttpService {
  http: HttpService;

  constructor() {
    this.http = new HttpService(`${getConfig().BACKEND_URL}/api`)
      .middlewares(authorizationEntraIDHeader)
      .catcherFallback(handleHttpErrors);
  }

  get request(): typeof this.http.request {
    return this.http.request;
  }
}
export const fortnoxHttpService = new FortnoxHttpService();
export const appHttpService = new AppHttpService();
export const authHttpService = new AuthHttpService();
